import React from 'react';
import {
  Paper,
  Typography,
  Grid,
  TextField,
  IconButton,
  Button,
  Box
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { FiPlus, FiTrash2 } from 'react-icons/fi';
import { FundingAllocation, RestrictionType } from '../../../../types/accounting';

interface FundingAllocationInputsProps {
  allocations: FundingAllocation[];
  onAllocationsChange: (allocations: FundingAllocation[]) => void;
  variant?: 'journal' | 'lineItem';
}

// Styled Components
const StyledPaper = styled(Paper)(({ theme }) => ({
  background: 'rgb(26, 31, 46)', // Dark navy background
  borderRadius: '12px',
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '3px',
    background: 'linear-gradient(90deg, #0EA5E9 0%, #10B981 100%)'
  }
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    backgroundColor: 'rgb(34, 39, 58)', // Slightly lighter than background
    borderRadius: '8px',
    '& fieldset': {
      borderColor: 'rgba(255, 255, 255, 0.12)',
    },
    '&:hover fieldset': {
      borderColor: 'rgba(255, 255, 255, 0.2)',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#0EA5E9',
    },
  },
  '& .MuiInputLabel-root': {
    color: 'rgba(255, 255, 255, 0.7)',
  },
  '& .MuiInputBase-input': {
    color: 'rgba(255, 255, 255, 0.9)',
  },
}));

const AddButton = styled(Button)(({ theme }) => ({
  backgroundColor: 'rgba(255, 255, 255, 0.05)',
  color: '#0EA5E9',
  padding: '8px 16px',
  borderRadius: '8px',
  textTransform: 'none',
  border: '1px solid rgba(14, 165, 233, 0.2)',
  '&:hover': {
    backgroundColor: 'rgba(14, 165, 233, 0.1)',
    borderColor: 'rgba(14, 165, 233, 0.3)',
  },
}));

const DeleteButton = styled(IconButton)(({ theme }) => ({
  color: '#ef4444',
  backgroundColor: 'rgba(239, 68, 68, 0.1)',
  '&:hover': {
    backgroundColor: 'rgba(239, 68, 68, 0.2)',
  },
  padding: '8px',
}));

const HeaderTypography = styled(Typography)(({ theme }) => ({
  color: 'rgba(255, 255, 255, 0.9)',
  fontWeight: 600,
  letterSpacing: '-0.02em',
  marginBottom: theme.spacing(3),
}));

export const FundingAllocationInputs: React.FC<FundingAllocationInputsProps> = ({
  allocations,
  onAllocationsChange,
  variant = 'journal'
}) => {
  const addAllocation = () => {
    const newAllocation: FundingAllocation = {
      fundId: '',
      sourceId: '',
      sourceType: variant === 'journal' ? 'journal' : 'lineItem',
      amount: 0,
      percentage: 0,
      restrictionType: RestrictionType.UNRESTRICTED
    };
    onAllocationsChange([...allocations, newAllocation]);
  };

  const removeAllocation = (index: number) => {
    const newAllocations = allocations.filter((_, i) => i !== index);
    onAllocationsChange(newAllocations);
  };

  const updateAllocation = (index: number, field: keyof FundingAllocation, value: string | number) => {
    const newAllocations = allocations.map((allocation, i) => 
      i === index ? { ...allocation, [field]: value } : allocation
    );
    onAllocationsChange(newAllocations);
  };

  return (
    <StyledPaper sx={{ p: 3 }}>
      <HeaderTypography variant="h6">
        Funding Allocations
      </HeaderTypography>
      
      {allocations.map((allocation, index) => (
        <Grid 
          container 
          spacing={2} 
          key={allocation.id} 
          sx={{ 
            mb: 2,
            backgroundColor: 'rgb(34, 39, 58)',
            borderRadius: '8px',
            p: 2,
            mx: 0,
            width: '100%'
          }}
        >
          <Grid item xs={12} md={5}>
            <StyledTextField
              fullWidth
              label="Fund"
              value={allocation.fundId}
              onChange={(e) => updateAllocation(index, 'fundId', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={5}>
            <StyledTextField
              fullWidth
              type="number"
              label="Percentage"
              value={allocation.percentage}
              onChange={(e) => updateAllocation(index, 'percentage', parseFloat(e.target.value))}
              inputProps={{ min: 0, max: 100 }}
            />
          </Grid>
          <Grid item xs={12} md={2} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <DeleteButton 
              onClick={() => removeAllocation(index)}
              size="small"
            >
              <FiTrash2 />
            </DeleteButton>
          </Grid>
        </Grid>
      ))}

      <Box sx={{ mt: 3 }}>
        <AddButton
          startIcon={<FiPlus />}
          onClick={addAllocation}
          size="small"
        >
          Add Allocation
        </AddButton>
      </Box>
    </StyledPaper>
  );
};

export default FundingAllocationInputs; 